import React from "react";
import { Button } from "../ui/Button";
import { type Table } from "@tanstack/react-table";

interface TablePaginationProps {
  table: Table<Record<string, any>>;
  currentPage?: number;
  totalPages?: number;
  totalElements?: number;
  onPageChange?: (page: number) => void;
  pageSize?: number;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  table,
  currentPage,
  totalPages,
  totalElements,
  onPageChange,
  pageSize = 10,
}) => {
  // Use server-side pagination if handlers are provided, otherwise use client-side
  const useServerPagination = onPageChange !== undefined;

  const handlePrevious = () => {
    if (useServerPagination && currentPage !== undefined) {
      onPageChange(currentPage - 1);
    } else {
      table.previousPage();
    }
  };

  const handleNext = () => {
    if (useServerPagination && currentPage !== undefined) {
      onPageChange(currentPage + 1);
    } else {
      table.nextPage();
    }
  };

  const canPrevious = useServerPagination ? (currentPage ?? 0) > 0 : table.getCanPreviousPage();
  const canNext = useServerPagination
    ? (currentPage ?? 0) < (totalPages ?? 1) - 1
    : table.getCanNextPage();

  const startItem = useServerPagination
    ? (currentPage ?? 0) * pageSize + 1
    : table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1;

  const endItem = useServerPagination
    ? Math.min(((currentPage ?? 0) + 1) * pageSize, totalElements ?? 0)
    : Math.min(
        (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
        table.getFilteredRowModel().rows.length
      );

  const total = useServerPagination
    ? (totalElements ?? 0)
    : table.getFilteredRowModel().rows.length;

  return (
    <div className="px-4 py-3 border-t border-border-gray sm:px-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <p className="text-sm text-black">
            Showing <span className="font-medium">{startItem}</span> to{" "}
            <span className="font-medium">{endItem}</span> of{" "}
            <span className="font-medium">{total}</span> results
            {useServerPagination && totalPages !== undefined && (
              <span className="ml-2 text-gray-600">
                (Page {(currentPage ?? 0) + 1} of {totalPages})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handlePrevious} disabled={!canPrevious}>
            Previous
          </Button>
          <Button variant="outline" size="sm" onClick={handleNext} disabled={!canNext}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
